/**
 * Create form for a level.
 */
import * as React from 'react';
import { FileText, ListOrdered } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { createLevel, getLevels, reorderLevels } from '@/services/levels.service';
import type { CreateLevelDto, Level } from '@/types/level.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { levelValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { LevelSortableList, NEW_LEVEL_PLACEHOLDER_ID } from './LevelSortableList';
import { LEVEL_CYCLE_FORM_OPTIONS, LEVEL_FORM_QUERY_KEYS } from './level-configs';
import {
  applyLevelFormField,
  emptyCreateLevelForm,
  getLevelFieldsToRevalidateAfterChange,
  parseOptionalInt,
  resolveCreatedLevelOrder,
  resolveEducationCycle,
} from './level-form-utils';

/** Props for {@link CreateLevelView}. */
interface CreateLevelViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create form for a level. */
export const CreateLevelView: React.FC<CreateLevelViewProps> = ({ onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = React.useState<CreateLevelDto>(emptyCreateLevelForm);
  /** Drag order including the create placeholder; `null` until the user reorders. */
  const [orderedLevelIds, setOrderedLevelIds] = React.useState<string[] | null>(null);

  const validation = useFormValidation(levelValidationSchema);

  const { data: allLevels = [] } = useQuery({
    queryKey: LEVEL_FORM_QUERY_KEYS.all,
    queryFn: () => getLevels({ pageSize: 10 }),
    select: (res) => res.data,
  });

  const createMutation = useMutation({
    mutationFn: async (dto: CreateLevelDto) => {
      const created: Level = await createLevel(dto);
      const finalOrder = resolveCreatedLevelOrder(
        orderedLevelIds,
        NEW_LEVEL_PLACEHOLDER_ID,
        created.id,
      );
      if (finalOrder) {
        await reorderLevels(finalOrder);
        await queryClient.invalidateQueries({ queryKey: LEVEL_FORM_QUERY_KEYS.list });
      }
      return created;
    },
    onSuccess: () => {
      toast.success('Niveau scolaire créé avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isSubmitting = createMutation.isPending;

  /** Patch one field; changing minAge also re-validates maxAge. */
  const handleFieldChange = (field: keyof CreateLevelDto, value: string | number | undefined) => {
    const updatedData = applyLevelFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);

    for (const extraField of getLevelFieldsToRevalidateAfterChange(field, formData)) {
      validation.validateField(extraField, formData[extraField], updatedData);
    }
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate then POST; mutation also applies drag order after create. */
  const handleSubmit = () => {
    if (!validation.validateForm(formData)) return;
    createMutation.mutate(formData);
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer un niveau scolaire"
      subtitle="Renseignez les informations du niveau scolaire"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isSubmitting}
      isValid={validation.isFormValid()}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code"
            value={formData.code}
            onChange={(e) => handleFieldChange('code', e.target.value)}
            onBlur={() => handleBlur('code')}
            placeholder="Ex: 6EME"
            maxLength={50}
            required
            helperText="Code unique identifiant le niveau"
            state={validation.getFieldState('code')}
            error={validation.getFieldError('code')}
          />

          <Input
            label="Libellé"
            value={formData.label}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            onBlur={() => handleBlur('label')}
            placeholder="Ex: Sixième"
            maxLength={255}
            required
            helperText="Nom complet du niveau"
            state={validation.getFieldState('label')}
            error={validation.getFieldError('label')}
          />
        </div>

        <Select
          label="Cycle éducatif"
          options={LEVEL_CYCLE_FORM_OPTIONS}
          value={formData.educationCycle || ''}
          placeholder="Sélectionner un cycle (optionnel)"
          onChange={(value) => handleFieldChange('educationCycle', resolveEducationCycle(value))}
          onBlur={() => handleBlur('educationCycle')}
          state={validation.getFieldState('educationCycle')}
          error={validation.getFieldError('educationCycle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Âge minimum"
            type="number"
            value={formData.minAge?.toString() || ''}
            onChange={(e) => handleFieldChange('minAge', parseOptionalInt(e.target.value))}
            onBlur={() => handleBlur('minAge')}
            placeholder="Ex: 11"
            min={3}
            max={25}
            state={validation.getFieldState('minAge')}
            error={validation.getFieldError('minAge')}
          />

          <Input
            label="Âge maximum"
            type="number"
            value={formData.maxAge?.toString() || ''}
            onChange={(e) => handleFieldChange('maxAge', parseOptionalInt(e.target.value))}
            onBlur={() => handleBlur('maxAge')}
            placeholder="Ex: 12"
            min={3}
            max={25}
            state={validation.getFieldState('maxAge')}
            error={validation.getFieldError('maxAge')}
          />
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <ListOrdered className="w-4 h-4" />
          Ordre d'affichage
        </h4>
        <p className="text-xs text-muted-foreground mb-3">
          Glissez-déposez pour ordonner les niveaux (le nouveau niveau apparaît en bleu)
        </p>
        <LevelSortableList
          allLevels={allLevels}
          currentLevelId={NEW_LEVEL_PLACEHOLDER_ID}
          newLevelLabel={formData.label}
          onOrderChange={setOrderedLevelIds}
        />
      </div>
    </SlidePanelForm>
  );
};
